import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Brain, Save, ToggleLeft, ToggleRight, Trash2, AlertTriangle, Info } from 'lucide-react';

// Phase 8 · Seller-Configured Objection Policies UI.
// SELLER-scope only in this v1 (no per-product / per-category overrides yet
// to keep the config surface small — scope_kind is always 'SELLER').
// Every policy has an explicit `enabled` flag. Saving is NOT the same
// as enabling — Sales Brain only gains authority when both are set.

const POLICY_META = {
  DISCOUNT_POLICY: {
    label: 'Discount Policy',
    hint: 'Maximum discount Sales Brain is authorised to offer on your behalf.',
  },
  MOQ_POLICY: {
    label: 'MOQ Exception Policy',
    hint: 'Lowest quantity you will accept below the product’s normal MOQ.',
  },
  PAYMENT_POLICY: {
    label: 'Payment Terms Policy',
    hint: 'Which payment methods Sales Brain is authorised to confirm.',
  },
  SHIPPING_POLICY: {
    label: 'Shipping Policy',
    hint: 'Shipping terms Sales Brain is authorised to quote.',
  },
};

const DEFAULT_CONFIGS = {
  DISCOUNT_POLICY: { maximum_discount_percent: 0 },
  MOQ_POLICY:      { minimum_exception_quantity: 0 },
  PAYMENT_POLICY:  { prepaid_allowed: true, cod_allowed: false,
                     advance_percent: null, credit_allowed: false,
                     credit_days: null },
  SHIPPING_POLICY: { free_shipping_enabled: false, free_shipping_threshold: null,
                     flat_shipping_enabled: false, flat_shipping_amount: null,
                     seller_handoff_required_when_unverified: true },
};

function Row({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-[12px] font-medium text-fab-text">{label}</span>
      {hint ? <span className="block text-[11px] text-fab-text-muted mb-1">{hint}</span> : null}
      {children}
    </label>
  );
}

function NumInput({ value, onChange, min = 0, max, step = 1, testid, placeholder }) {
  return (
    <input
      type="number"
      value={value === null || value === undefined ? '' : value}
      onChange={(e) => {
        const v = e.target.value === '' ? null : Number(e.target.value);
        onChange(v);
      }}
      min={min} max={max} step={step}
      placeholder={placeholder}
      className="mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
      data-testid={testid}
    />
  );
}

function Toggle({ on, onChange, testid, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={`inline-flex items-center gap-2 text-[13px] ${on ? 'text-emerald-700' : 'text-neutral-500'}`}
      data-testid={testid}
    >
      {on ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
      {label}
    </button>
  );
}

export default function SalesBrainPolicies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [drafts, setDrafts] = useState({}); // {policy_type: {config, enabled, expires_at}}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sales-brain/policies');
      setItems(data.items || []);
      // Seed drafts from server so users can toggle/edit
      const seeded = {};
      (data.items || []).forEach((p) => {
        if (p.scope_kind === 'SELLER') {
          seeded[p.policy_type] = {
            config: { ...(p.config || {}) },
            enabled: !!p.enabled,
            expires_at: p.expires_at || '',
          };
        }
      });
      setDrafts(seeded);
    } catch (e) {
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const readOnlyPolicy = (policy_type) => {
    // If the SELLER-scope policy exists on the server, treat that as source of truth.
    const server = items.find((p) => p.policy_type === policy_type && p.scope_kind === 'SELLER');
    const draft = drafts[policy_type];
    const merged = draft || {
      config: { ...(DEFAULT_CONFIGS[policy_type] || {}) },
      enabled: false, expires_at: '',
    };
    return { server, draft: merged };
  };

  const patchDraft = (policy_type, patch) => {
    setDrafts((d) => ({
      ...d,
      [policy_type]: {
        ...(d[policy_type] || {
          config: { ...(DEFAULT_CONFIGS[policy_type] || {}) },
          enabled: false, expires_at: '',
        }),
        ...patch,
      },
    }));
  };

  const patchConfig = (policy_type, patch) => {
    setDrafts((d) => {
      const cur = d[policy_type] || {
        config: { ...(DEFAULT_CONFIGS[policy_type] || {}) },
        enabled: false, expires_at: '',
      };
      return {
        ...d,
        [policy_type]: { ...cur, config: { ...(cur.config || {}), ...patch } },
      };
    });
  };

  const save = async (policy_type) => {
    const draft = drafts[policy_type];
    if (!draft) return;
    setSaving(policy_type);
    try {
      const body = {
        policy_type,
        config: draft.config || {},
        enabled: !!draft.enabled,
        scope_kind: 'SELLER',
        expires_at: draft.expires_at || null,
      };
      await api.put('/sales-brain/policies', body);
      toast.success(`${POLICY_META[policy_type].label} saved`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to save policy';
      toast.error(msg);
    } finally {
      setSaving('');
    }
  };

  const remove = async (policy_type) => {
    if (!window.confirm(`Delete ${POLICY_META[policy_type].label}?`)) return;
    try {
      await api.delete(`/sales-brain/policies?policy_type=${policy_type}`);
      toast.success('Policy removed');
      setDrafts((d) => { const nd = { ...d }; delete nd[policy_type]; return nd; });
      await load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="sales-brain-policies">
      <div className="flex items-start gap-2">
        <Brain className="w-5 h-5 text-fab-primary mt-1" />
        <div>
          <div className="text-[15px] font-semibold text-fab-text">Sales Brain — Objection Policies</div>
          <div className="text-[12px] text-fab-text-muted mt-0.5">
            Grant Sales Brain explicit authority to resolve buyer objections.
            Each policy is <b>disabled by default</b>. Sales Brain only
            gains authority when a policy is enabled AND within its
            configured limits. Otherwise the buyer is handed off to you.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-[12px] text-fab-text-muted p-4">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* DISCOUNT */}
          {(() => {
            const { server, draft } = readOnlyPolicy('DISCOUNT_POLICY');
            return (
              <PolicyCard
                testid="policy-card-discount"
                title={POLICY_META.DISCOUNT_POLICY.label}
                hint={POLICY_META.DISCOUNT_POLICY.hint}
                enabled={draft.enabled}
                onToggle={(v) => patchDraft('DISCOUNT_POLICY', { enabled: v })}
                onSave={() => save('DISCOUNT_POLICY')}
                onDelete={server ? () => remove('DISCOUNT_POLICY') : null}
                saving={saving === 'DISCOUNT_POLICY'}
                expires={draft.expires_at}
                setExpires={(v) => patchDraft('DISCOUNT_POLICY', { expires_at: v })}
              >
                <Row label="Maximum discount %"
                     hint="Sales Brain will only quote a discount at or below this ceiling.">
                  <NumInput
                    value={draft.config.maximum_discount_percent}
                    onChange={(v) => patchConfig('DISCOUNT_POLICY', { maximum_discount_percent: v })}
                    min={0} max={100} step={0.5}
                    testid="policy-discount-percent"
                    placeholder="0 – 100"
                  />
                </Row>
              </PolicyCard>
            );
          })()}

          {/* MOQ */}
          {(() => {
            const { server, draft } = readOnlyPolicy('MOQ_POLICY');
            return (
              <PolicyCard
                testid="policy-card-moq"
                title={POLICY_META.MOQ_POLICY.label}
                hint={POLICY_META.MOQ_POLICY.hint}
                enabled={draft.enabled}
                onToggle={(v) => patchDraft('MOQ_POLICY', { enabled: v })}
                onSave={() => save('MOQ_POLICY')}
                onDelete={server ? () => remove('MOQ_POLICY') : null}
                saving={saving === 'MOQ_POLICY'}
                expires={draft.expires_at}
                setExpires={(v) => patchDraft('MOQ_POLICY', { expires_at: v })}
              >
                <Row label="Minimum quantity you'll accept"
                     hint="Product MOQ still applies. This is the floor Sales Brain may confirm below MOQ.">
                  <NumInput
                    value={draft.config.minimum_exception_quantity}
                    onChange={(v) => patchConfig('MOQ_POLICY', { minimum_exception_quantity: v })}
                    min={1} step={1}
                    testid="policy-moq-qty"
                    placeholder="e.g. 20"
                  />
                </Row>
              </PolicyCard>
            );
          })()}

          {/* PAYMENT */}
          {(() => {
            const { server, draft } = readOnlyPolicy('PAYMENT_POLICY');
            const cfg = draft.config || {};
            return (
              <PolicyCard
                testid="policy-card-payment"
                title={POLICY_META.PAYMENT_POLICY.label}
                hint={POLICY_META.PAYMENT_POLICY.hint}
                enabled={draft.enabled}
                onToggle={(v) => patchDraft('PAYMENT_POLICY', { enabled: v })}
                onSave={() => save('PAYMENT_POLICY')}
                onDelete={server ? () => remove('PAYMENT_POLICY') : null}
                saving={saving === 'PAYMENT_POLICY'}
                expires={draft.expires_at}
                setExpires={(v) => patchDraft('PAYMENT_POLICY', { expires_at: v })}
              >
                <div className="grid grid-cols-1 gap-3">
                  <Toggle on={!!cfg.prepaid_allowed}
                          onChange={(v) => patchConfig('PAYMENT_POLICY', { prepaid_allowed: v })}
                          testid="policy-payment-prepaid"
                          label="Prepaid (UPI / Bank transfer)" />
                  <Toggle on={!!cfg.cod_allowed}
                          onChange={(v) => patchConfig('PAYMENT_POLICY', { cod_allowed: v })}
                          testid="policy-payment-cod"
                          label="Cash on Delivery" />
                  <Row label="Advance % (0–100, optional)"
                       hint="If you require partial upfront payment.">
                    <NumInput
                      value={cfg.advance_percent}
                      onChange={(v) => patchConfig('PAYMENT_POLICY', { advance_percent: v })}
                      min={0} max={100} step={1}
                      testid="policy-payment-advance"
                      placeholder="e.g. 30"
                    />
                  </Row>
                  <Toggle on={!!cfg.credit_allowed}
                          onChange={(v) => patchConfig('PAYMENT_POLICY', { credit_allowed: v })}
                          testid="policy-payment-credit"
                          label="Credit terms (structured, no third-party finance)" />
                  {cfg.credit_allowed && (
                    <Row label="Credit days">
                      <NumInput
                        value={cfg.credit_days}
                        onChange={(v) => patchConfig('PAYMENT_POLICY', { credit_days: v })}
                        min={0} max={180} step={1}
                        testid="policy-payment-credit-days"
                        placeholder="e.g. 15"
                      />
                    </Row>
                  )}
                  <div className="flex items-start gap-1.5 text-[11px] text-fab-text-muted">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    Sales Brain will only confirm payment methods that FABTEXS supports.
                  </div>
                </div>
              </PolicyCard>
            );
          })()}

          {/* SHIPPING */}
          {(() => {
            const { server, draft } = readOnlyPolicy('SHIPPING_POLICY');
            const cfg = draft.config || {};
            return (
              <PolicyCard
                testid="policy-card-shipping"
                title={POLICY_META.SHIPPING_POLICY.label}
                hint={POLICY_META.SHIPPING_POLICY.hint}
                enabled={draft.enabled}
                onToggle={(v) => patchDraft('SHIPPING_POLICY', { enabled: v })}
                onSave={() => save('SHIPPING_POLICY')}
                onDelete={server ? () => remove('SHIPPING_POLICY') : null}
                saving={saving === 'SHIPPING_POLICY'}
                expires={draft.expires_at}
                setExpires={(v) => patchDraft('SHIPPING_POLICY', { expires_at: v })}
              >
                <div className="grid grid-cols-1 gap-3">
                  <Toggle on={!!cfg.free_shipping_enabled}
                          onChange={(v) => patchConfig('SHIPPING_POLICY', { free_shipping_enabled: v })}
                          testid="policy-shipping-free-enabled"
                          label="Offer free shipping above a threshold" />
                  {cfg.free_shipping_enabled && (
                    <Row label="Free shipping threshold (₹)">
                      <NumInput
                        value={cfg.free_shipping_threshold}
                        onChange={(v) => patchConfig('SHIPPING_POLICY', { free_shipping_threshold: v })}
                        min={0} step={100}
                        testid="policy-shipping-free-threshold"
                        placeholder="e.g. 5000"
                      />
                    </Row>
                  )}
                  <Toggle on={!!cfg.flat_shipping_enabled}
                          onChange={(v) => patchConfig('SHIPPING_POLICY', { flat_shipping_enabled: v })}
                          testid="policy-shipping-flat-enabled"
                          label="Charge a flat shipping fee" />
                  {cfg.flat_shipping_enabled && (
                    <Row label="Flat shipping amount (₹)">
                      <NumInput
                        value={cfg.flat_shipping_amount}
                        onChange={(v) => patchConfig('SHIPPING_POLICY', { flat_shipping_amount: v })}
                        min={0} step={10}
                        testid="policy-shipping-flat-amount"
                        placeholder="e.g. 150"
                      />
                    </Row>
                  )}
                  <Toggle on={!!cfg.seller_handoff_required_when_unverified}
                          onChange={(v) => patchConfig('SHIPPING_POLICY', { seller_handoff_required_when_unverified: v })}
                          testid="policy-shipping-handoff"
                          label="Always hand off shipping questions when unverified" />
                  <div className="flex items-start gap-1.5 text-[11px] text-fab-text-muted">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    Sales Brain will never invent shipping costs or delivery dates.
                  </div>
                </div>
              </PolicyCard>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function PolicyCard({ testid, title, hint, enabled, onToggle, onSave, onDelete,
                       saving, expires, setExpires, children }) {
  return (
    <section className="fab-card p-5 space-y-4" data-testid={testid}>
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[14px] font-semibold text-fab-text">{title}</div>
          <div className="text-[11px] text-fab-text-muted mt-0.5 max-w-md">{hint}</div>
        </div>
        <Toggle on={enabled} onChange={onToggle}
                testid={`${testid}-enabled`}
                label={enabled ? 'Enabled' : 'Disabled'} />
      </header>

      <div className="space-y-3">{children}</div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-neutral-100">
        <Row label="Expires at (optional)" hint="Leave blank for no expiry.">
          <input
            type="datetime-local"
            value={expires ? expires.slice(0, 16) : ''}
            onChange={(e) => {
              const val = e.target.value ? new Date(e.target.value).toISOString() : '';
              setExpires(val);
            }}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
            data-testid={`${testid}-expires`}
          />
        </Row>
        <div className="flex items-end justify-end gap-2">
          {onDelete && (
            <button type="button" onClick={onDelete}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] text-red-600 hover:bg-red-50"
                    data-testid={`${testid}-delete`}>
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
          <button type="button" onClick={onSave} disabled={!!saving}
                  className="btn-primary text-[13px] inline-flex items-center gap-1.5"
                  data-testid={`${testid}-save`}>
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save policy'}
          </button>
        </div>
      </div>
    </section>
  );
}
